import { useEffect, useMemo, useState } from 'react';
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import {
  Button,
  HookedForm,
  InputField,
  SelectField,
  CheckSelectField,
  DesignPreviewDialog,
  DesignTemplatePicker,
  useDesignTemplateFields,
} from '@app/components';
import { SwitchField } from '@app/components/Fields/SwitchField';
import { useNavigate } from 'react-router-dom';
import { formatDateToISO } from '@app/utils/utils';
import { toast } from 'react-toastify';
import AddIcon from '@app/assets/icons/add.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { resetForm, updateFormData } from '@app/redux/features/automationForm';
import { TemplateList } from '@app/components/TemplateList';
import { useFormChangeDetection } from './useFormChangeDetection';
import DraftIcon from '@app/assets/icons/draft.svg?react';
import eyeIcon from '@app/assets/icons/eye.png';
import { ListInterface, TagInterface } from '@app/models';
import { MessageTemplateChannel } from '@app/utils/consts';
import { useQuery } from '@tanstack/react-query';
import { getLists } from '@app/services/list';
import { getTag } from '@app/services/tag';
import {createAutomation, updateAutomation} from "@app/services/automations.ts";
import {AUTOMATION_EVENT_NAME_VALUE, AUTOMATION_EVENT_SOURCE_VALUE} from "@app/models/automations.ts";
import {omit} from "lodash";

const FORM_STORAGE_KEY = 'automationFormData';

const EVENT_SOURCE_OPTIONS = [
  {
    label: 'Echo Ratings',
    value: AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM,
  },
  // {
  //   label: 'Google',
  //   value: AUTOMATION_EVENT_SOURCE_VALUE.GOOGLE_REVIEWS,
  // },
  {
    label: 'Jobber',
    value: AUTOMATION_EVENT_SOURCE_VALUE.JOBBER,
  },
  {
    label: 'Square Up',
    value: AUTOMATION_EVENT_SOURCE_VALUE.SQUAREUP,
  },
];

const EVENT_NAME_OPTIONS = [
  {
    label: 'Customer added',
    value: AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED,
    source: [AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM, AUTOMATION_EVENT_SOURCE_VALUE.JOBBER, AUTOMATION_EVENT_SOURCE_VALUE.SQUAREUP],
  },
  // {
  //   label: 'Customer deleted',
  //   value: AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED,
  //   source: AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM,
  // },
  // {
  //   label: 'Review added',
  //   value: AUTOMATION_EVENT_NAME_VALUE.REVIEW_ADDED,
  //   source: AUTOMATION_EVENT_SOURCE_VALUE.GOOGLE_REVIEWS,
  // },
  // {
  //   label: 'Review updated',
  //   value: AUTOMATION_EVENT_NAME_VALUE.REVIEW_UPDATED,
  //   source: AUTOMATION_EVENT_SOURCE_VALUE.GOOGLE_REVIEWS,
  // },
];

export const CreateAutomationConfigAutomation = ({
  form,
}: {
  form: UseFormReturn<FieldValues, unknown, undefined>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getValues, handleSubmit } = form;
  const [followUpRows, setFollowUpRows] = useState([0]);
  const formData = useAppSelector((state) => state.campaignForm.formData);
  const { data: listsAndTags } = useQuery({
    queryKey: ['all-lists-and-tags'],
    queryFn: async () =>
      await Promise.all([getLists(), getTag()])
        .then((responses) => ({
          lists: responses?.[0]?.data?.lists,
          tags: responses?.[1]?.data?.tags,
        }))
        .catch((err) => err),
  });

  const listOptions = useMemo(
    () =>
      listsAndTags?.lists?.map((item: ListInterface) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [listsAndTags?.lists]
  );
  const tagOptions = useMemo(
    () =>
      listsAndTags?.tags?.map((item: TagInterface) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [listsAndTags?.tags]
  );

  // Chained selects: filter event options by the selected source.
  // useWatch (not form.watch) subscribes THIS component to changes, since
  // `form` is passed in as a prop and form.watch would not re-render here.
  const selectedEventSource = useWatch({ control: form.control, name: 'eventSource' });
  const selectedEventName = useWatch({ control: form.control, name: 'eventName' });
  const filteredEventOptions = useMemo(
    () => EVENT_NAME_OPTIONS.filter(
        (option) => option.source.includes(selectedEventSource)
    ),
    [selectedEventSource]
  );
  const hasCustomerFilter = useMemo(
    () => (
        [AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED, AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED].includes(selectedEventName)
        &&
        [AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM].includes(selectedEventSource)
    ),
    [selectedEventName, selectedEventSource]
  );
  const hasRatingConfig = useMemo(
    () => [AUTOMATION_EVENT_NAME_VALUE.REVIEW_ADDED, AUTOMATION_EVENT_NAME_VALUE.REVIEW_UPDATED].includes(selectedEventName),
    [selectedEventName]
  );

  // Reset the event when the source changes if the currently selected event
  // no longer belongs to the selected source. Reset to '' (a defined value)
  // rather than undefined so the field stays controlled.
  useEffect(() => {
    const currentEvent = form.getValues('eventName');
    if (
      currentEvent &&
      !filteredEventOptions.some((option) => option.value === currentEvent)
    ) {
      form.setValue('eventName', '');
    }
  }, [selectedEventSource, filteredEventOptions, form]);

  // Use the custom hook for form change detection
  const { hasUnsavedChanges, resetUnsavedChanges } =
    useFormChangeDetection(form);

  // `null` template id = business default. Custom designs take priority over the linked template when sending
  const selectedChannels = useWatch({ control: form.control, name: 'channels' }) as string[] | undefined;
  const {
    emailTemplateId,
    smsTemplateId,
    customEmailDesign,
    customMessageDesign,
    emailDesign,
    smsDesign,
    setTemplateId,
    resetCustomDesign,
    editingDesign,
    startEditing,
    closeDesignEditor,
    handleTemplateLinkError,
    previewChannel,
    setPreviewChannel,
  } = useDesignTemplateFields(form, FORM_STORAGE_KEY);
  // Without a channel selection both pickers are shown, as before
  const showEmailTemplate = !selectedChannels?.length || selectedChannels.includes('email');
  const showSmsTemplate = !selectedChannels?.length || selectedChannels.includes('sms');

  // Override the navigate function to check for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Initialize form with saved data for this step
  useEffect(() => {
    // First check localStorage for saved form data
    const storedFormData = localStorage.getItem('automationFormData');
    if (storedFormData) {
      const parsedData = JSON.parse(storedFormData);
      Object.keys(parsedData).forEach((key) => {
        if (parsedData[key] !== undefined) {
          form.setValue(key, parsedData[key]);
        }
      });

      // If follow-up rows were previously set, restore them
      const savedFollowUpDays1 = parsedData.followUpDays1;
      const savedFollowUpDays2 = parsedData.followUpDays2;

      if (savedFollowUpDays1) {
        setFollowUpRows([0, 1]);
      }
      if (savedFollowUpDays2) {
        setFollowUpRows([0, 1, 2]);
      }
    }
    // Then check Redux state if localStorage didn't have data
    else if (formData) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined) {
          form.setValue(key, formData[key]);
        }
      });

      // If follow-up rows were previously set, restore them
      const savedFollowUpDays1 = formData.followUpDays1;
      const savedFollowUpDays2 = formData.followUpDays2;

      if (savedFollowUpDays1) {
        setFollowUpRows([0, 1]);
      }
      if (savedFollowUpDays2) {
        setFollowUpRows([0, 1, 2]);
      }
    }

  }, [form, formData]);

  const addFollowUpRow = () => {
    if (followUpRows.length < 3) {
      setFollowUpRows([...followUpRows, followUpRows.length]);
    }
  };

  const removeFollowUpRow = (index: number) => {
    setFollowUpRows(followUpRows.filter((_, i) => i !== index));

    // Clear the form values for the removed row
    const rowNumber = followUpRows[index];
    form.setValue(`followUpDays${rowNumber}`, undefined);
    form.setValue(`followUpPeriod${rowNumber}`, undefined);

    // Save the updated form data
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('automationFormData', JSON.stringify(values));
  };

  const handleBack = () => {
    // Save current form data before navigating
    const values = form.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('automationFormData', JSON.stringify(values));
    navigate('/automations/create/settings');
  };

  const validateFollowUpFields = (formData: FieldValues) => {
    const missingFollowUpFields =
      formData['sendFollow'] &&
      followUpRows.some((rowIndex) => {
        const days = formData[`followUpDays${rowIndex}`];
        const period = formData[`followUpPeriod${rowIndex}`];
        return (
          days === undefined ||
          days === '' ||
          period === undefined ||
          period === ''
        );
      });
    if (missingFollowUpFields) {
      toast.error(
        'Please provide both the number of days and the period for each follow-up.'
      );
      return false;
    }
    const invalidFollowUpDays = followUpRows.some((rowIndex) => {
      const days = formData[`followUpDays${rowIndex}`];
      return days > 3; // 3 days = 72 hours
    });
    if (invalidFollowUpDays) {
      toast.error('Follow-up days cannot exceed 72 hours (3 days).');
      return false;
    }
    return true;
  };



  // Custom validation function for template fields
  const prepareEventConfig = (formData: any) => {
    switch (formData.eventName) {
      case AUTOMATION_EVENT_NAME_VALUE.REVIEW_ADDED:
      case AUTOMATION_EVENT_NAME_VALUE.REVIEW_UPDATED:
        return {ratings: formData.ratingConfig};
      default:
        break;
    }

    return null;
  };


  const onSubmit = async (statusOverride?: string) => {
    // Check if we're editing an existing automation
    const formData = getValues();
    const isEditing = formData.automationId;
    const isDraft = statusOverride === 'DRAFT';

    if (formData.automationName === '' || !formData.automationName) {
      toast.error('Automation Settings is required.');
      navigate('/automations/create/settings');
      return;
    }

    try {
      setIsSubmitting(true);

      const validFollowup = validateFollowUpFields(formData);
      if (!validFollowup) {
        setIsSubmitting(false);
        return;
      }

      // Save one last time before submission
      // Merge current form values with any previously saved template fields
      const existingRaw = localStorage.getItem('automationFormData');
      const existingStored = existingRaw ? JSON.parse(existingRaw) : {};
      const mergedFormData = { ...existingStored, ...(omit(formData, ['emailDesignContent', 'messageDesignContent', ])) };
      //
      // console.log('existingStored', existingStored);
      // console.log('formData', formData);
      // console.log('mergedFormData', mergedFormData);

      dispatch(updateFormData(mergedFormData));
      localStorage.setItem('automationFormData', JSON.stringify(mergedFormData));
      // Only process follow-up data for active rows with values
      const followUpNumbers = followUpRows
        .map((rowIndex) => formData[`followUpDays${rowIndex}`])
        .filter(
          (value) => value !== undefined && value !== '' && value !== null
        )
        .map((value) => value.toString());
      const followUpPeriods = followUpRows
        .map((rowIndex) => formData[`followUpPeriod${rowIndex}`])
        .filter(
          (value) => value !== undefined && value !== '' && value !== null
        )
        .map((value) => value.toString());

      // Get the updated email template from EmailDesignEditor if available
      const storedFormData = localStorage.getItem('automationFormData');
      const parsedStoredData = storedFormData ? JSON.parse(storedFormData) : {};

      const emailDesignContent = parsedStoredData.emailDesignContent || {};
      const messageDesignContent = parsedStoredData.messageDesignContent || {};
      // const updatedEmailTemplate = parsedStoredData.updatedEmailTemplate || editedTemplateHtml;
      // const updatedMessageTemplate = parsedStoredData.updatedMessageTemplate || '';
      // const updatedReviewFormTemplate = parsedStoredData.updatedReviewFormTemplate || '';
      // const updatedFeedbackFormTemplate = parsedStoredData.updatedFeedbackFormTemplate || '';
      // const updatedPositiveWindowTemplate = parsedStoredData.updatedPositiveWindowTemplate || '';
      // const updatedNegativeWindowTemplate = parsedStoredData.updatedNegativeWindowTemplate || '';
      // const reviewFormDesignContent = parsedStoredData.reviewFormDesignContent || {};
      // const feedbackFormDesignContent = parsedStoredData.feedbackFormDesignContent || {};
      // const positiveWindowDesignContent = parsedStoredData.positiveWindowDesignContent || {};
      // const negativeWindowDesignContent = parsedStoredData.negativeWindowDesignContent || {};

      const automationData = {
        name: formData.automationName,
        description: formData.description,
        type: formData.type,
        channels: formData.channels,
        // template: {
        //   subject: emailDesignContent.subject || 'How was your experience?',
        //   emailTemplate: encodeURIComponent(updatedEmailTemplate) || '', // Email design HTML template
        //   textTemplate: encodeURIComponent(updatedMessageTemplate) || '', // Only message design HTML template
        //   reviewFormTemplate: encodeURIComponent(updatedReviewFormTemplate) || '', // Review form design HTML template
        //   feedbackFormTemplate: encodeURIComponent(updatedFeedbackFormTemplate) || '', // Feedback form design HTML template
        //   positiveWindowTemplate: encodeURIComponent(updatedPositiveWindowTemplate) || '', // Positive window design HTML template
        //   negativeWindowTemplate: encodeURIComponent(updatedNegativeWindowTemplate) || '', // Negative window design HTML template
        // },
        // emailDesignContent: emailDesignContent,
        emailDesignContent: Object.keys(emailDesignContent).length ? emailDesignContent : null,
        messageDesignContent: Object.keys(messageDesignContent).length ? messageDesignContent : null,
        // Sent on every save: an omitted id keeps the old link on update, `null` = business default
        emailTemplateId: formData.emailTemplateId ?? null,
        smsTemplateId: formData.smsTemplateId ?? null,
        sendFollowUp: formData.sendFollowUp,
        followUpRows: followUpNumbers,
        followUpPeriods: followUpPeriods,
        followUpConfig: null,
        followUps: prepareEventConfig(formData),
        lists: hasCustomerFilter ? formData.lists : null,
        tags: hasCustomerFilter ? formData.tags : null,
        isDraft: isDraft,
        senderName: formData.businessName,
        senderAddress: formData.businessAddress,
        senderEmail: formData.businessEmail,
        senderPhone: formData.echoRatingsPhoneNumber,
        // reviewFormDesign: reviewFormDesignContent, // Review form design configuration
        // feedbackFormDesign: feedbackFormDesignContent, // Feedback form design configuration
        // positiveWindowDesign: positiveWindowDesignContent, // Positive window design configuration
        // negativeWindowDesign: negativeWindowDesignContent, // Negative window design configuration


        eventSource: formData.eventSource,
        eventName: formData.eventName,
        eventConfig: prepareEventConfig(formData),
      };


      // console.log('isEditing', isEditing);
      // console.log('automationData', automationData);
      // console.log('parsedStoredData', parsedStoredData);

      if (isEditing) {
        // Update existing campaign
        await updateAutomation(formData.automationId, automationData);

        toast.success('Automation updated successfully');
      } else {
        // Create new campaign
        // const response = await createCampaign(automationData);
        const response = await createAutomation(automationData);
        const newCampaignId =
          response.data?.campaign?.id ||
          response.data?.data?.id ||
          response.data?.id;
        toast.success('Automation created successfully');

        // Clear form state
        form.reset();

        // Reset form data in Redux
        dispatch(resetForm());

        // Clear localStorage completely
        localStorage.removeItem('automationFormData');
        localStorage.removeItem('automationSelectedLists');

        // Reset unsaved changes flag
        resetUnsavedChanges();

        // Navigate back to campaigns list with highlight
        if (newCampaignId) {
          navigate(`/automations?highlight=${newCampaignId}`);
        } else {
          navigate('/automations');
        }
        return;
      }

      // Clear form state
      form.reset();

      // Reset form data in Redux
      dispatch(resetForm());

      // Clear localStorage completely
      localStorage.removeItem('automationFormData');
      localStorage.removeItem('automationSelectedLists');

      // Reset unsaved changes flag
      resetUnsavedChanges();

      // Navigate back to campaigns list
      navigate('/automations');
    } catch (error: any) {
      console.error('Error creating/updating automation:', error);
      // Handled here: a deleted linked template resets the channel; plan limits are already shown by the api interceptor
      if (handleTemplateLinkError(error) || error?.data?.name === 'plan_exeeded') return;
      if (error?.data?.error?.message)
        toast.error(
          isEditing ? error?.data?.error?.message : error?.data?.error?.message,
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
      else
        toast.error(
          isEditing ? 'Failed to update automation' : 'Failed to create automation',
          {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          }
        );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Watch for changes in the sendImmediately field
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'sendImmediately') {
        if (value.sendImmediately) {
          const formattedDate = formatDateToISO(new Date());
          form.setValue('initialSendDate', formattedDate);
        }
      }
    });

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe();
  }, [form]);

  if (editingDesign) {
    return (
      <div className="w-full h-screen bg-white overflow-hidden">
        <TemplateList
          open
          onClose={closeDesignEditor}
          // Only the legacy HTML editor inside TemplateList calls this; automations send the design content instead
          onSelectTemplate={() => {}}
          templateMode={editingDesign.channel === MessageTemplateChannel.EMAIL ? 'email' : 'text'}
          storageProperty={FORM_STORAGE_KEY}
          initialEditingType={editingDesign.channel === MessageTemplateChannel.EMAIL ? 'email' : 'message'}
          initialEmailContent={editingDesign.channel === MessageTemplateChannel.EMAIL ? editingDesign.content : undefined}
          initialMessageContent={editingDesign.channel === MessageTemplateChannel.SMS ? editingDesign.content : undefined}
        />
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto mb-24 p-4 flex flex-col justify-between items-center gap-4">
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Review request automation</h4>
            <p className="text-xs text-base-coolGray">
              Set the conditions under which this automation is activated
            </p>
          </div>
          <hr />
          <HookedForm formHook={form} className="space-y-4">
            <div className="flex flex-nowrap items-start justify-stretch gap-3 bg-base-seasalt rounded-sm p-4">

              <SelectField
                  bgClass="bg-white"
                  placeholder="Select a source"
                  name="eventSource"
                  label="Source"
                  options={EVENT_SOURCE_OPTIONS}
              />
              <SelectField
                  bgClass="bg-white"
                  placeholder="Select a event"
                  name="eventName"
                  label="Event name"
                  options={filteredEventOptions}
              />
            </div>
            {hasCustomerFilter && (
              <>

                <hr />
                <div className="mt-3">
                  <CheckSelectField
                      name="lists"
                      label="Lists"
                      isOptional
                      options={listOptions}
                      placeholder="All lists"
                  />
                </div>
                <div className="mt-3">
                  <CheckSelectField
                      name="tags"
                      label="Tags"
                      isOptional
                      options={tagOptions}
                      placeholder="All tags"
                  />
                </div>
              </>
            )}
            {hasRatingConfig && (
              <>

                <hr />
                <div className="mt-3">
                  <CheckSelectField
                      name="ratingConfig"
                      label="Rating"
                      isOptional
                      options={[
                        {
                          label: '1 Star',
                          value: '1',
                        },
                        {
                          label: '2 Stars',
                          value: '2',
                        },
                        {
                          label: '3 Stars',
                          value: '3',
                        },
                        {
                          label: '4 Stars',
                          value: '4',
                        },
                        {
                          label: '5 Stars',
                          value: '5',
                        },
                      ]}
                      placeholder="Any"
                  />
                </div>
              </>
            )}
          </HookedForm>
        </div>
        {/*<div className="bg-white w-full max-w-[800px] p-4 space-y-4">*/}
        {/*  <div>*/}
        {/*    <h4 className="text-sm font-semibold">Initial sending</h4>*/}
        {/*    <p className="text-xs text-base-coolGray">*/}
        {/*      Set a date for your first send of this campaign*/}
        {/*    </p>*/}
        {/*  </div>*/}
        {/*  <hr />*/}
        {/*  <HookedForm formHook={form} className="space-y-4">*/}
        {/*    <SwitchField*/}
        {/*        name="sendImmediately"*/}
        {/*        label={<span>Send immediately</span>}*/}
        {/*    />*/}
        {/*    <CustomDatePicker*/}
        {/*        name="initialSendDate"*/}
        {/*        label="When should we send the initial campaign? "*/}
        {/*        preventPastDates={true}*/}
        {/*        position="right"*/}
        {/*        sendImmediately={sendImmediatelyValue}*/}
        {/*    />*/}
        {/*  </HookedForm>*/}
        {/*</div>*/}
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Design templates</h4>
            <p className="text-xs text-base-coolGray">
              Choose a design template for windows or create a new one
            </p>
          </div>
          <hr />
          <div className="space-y-4">
            {showEmailTemplate && (
              <DesignTemplatePicker
                channel={MessageTemplateChannel.EMAIL}
                label="Email template"
                value={emailTemplateId}
                onChange={(templateId) => setTemplateId(MessageTemplateChannel.EMAIL, templateId)}
                customContent={customEmailDesign}
                onEdit={(content) => startEditing({ channel: MessageTemplateChannel.EMAIL, content })}
                onReset={() => resetCustomDesign(MessageTemplateChannel.EMAIL)}
              />
            )}
            {showSmsTemplate && (
              <DesignTemplatePicker
                channel={MessageTemplateChannel.SMS}
                label="Text template"
                value={smsTemplateId}
                onChange={(templateId) => setTemplateId(MessageTemplateChannel.SMS, templateId)}
                customContent={customMessageDesign}
                onEdit={(content) => startEditing({ channel: MessageTemplateChannel.SMS, content })}
                onReset={() => resetCustomDesign(MessageTemplateChannel.SMS)}
              />
            )}
          </div>
        </div>
        <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold">Follow-ups</h4>
            <p className="text-xs text-base-coolGray">
              You can also send follow-up requests after a while to customers
              who have not left a review.
            </p>
          </div>
          <hr />
          <HookedForm formHook={form} className="space-y-4">
            <SwitchField name="sendFollowUp" label={<span>Send follow-up</span>}/>
            <p className="text-sm">Send follow-up</p>

            {followUpRows.map((rowIndex, index) => (
              <div key={rowIndex} className="flex gap-4 items-center">
                <InputField
                  name={`when${index}`}
                  value="After"
                  disabled
                  className="!w-[100px] opacity-90 shrink-0"
                  containerClass="!w-fit"
                />
                <input
                  type="number"
                  className="!w-16 bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1"
                  min="1"
                  max="3"
                  {...form.register(`followUpDays${rowIndex}`)}
                  onChange={(e) => {
                    form.register(`followUpDays${rowIndex}`).onChange(e);
                    // Save form data immediately
                    const values = form.getValues();
                    dispatch(updateFormData(values));
                    localStorage.setItem(
                      'automationFormData',
                      JSON.stringify(values)
                    );
                  }}
                />
                <SelectField
                  options={[{ label: 'Day(s)', value: 'days' }]}
                  name={`followUpPeriod${rowIndex}`}
                  defaultValue={'days'}
                />

                <div className="flex gap-2 ml-2">
                  <button
                    type="button"
                    onClick={() => setPreviewChannel(MessageTemplateChannel.EMAIL)}
                    className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px] hover:bg-[#D1D5DB] transition-colors"
                  >
                    <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2" />
                    Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewChannel(MessageTemplateChannel.SMS)}
                    className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px] hover:bg-[#D1D5DB] transition-colors"
                  >
                    <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2" />
                    Text
                  </button>
                </div>

                {index === followUpRows.length - 1 &&
                  followUpRows.length < 3 && (
                    <Button
                      kind="secondary"
                      type="button"
                      className="!min-w-fit"
                      onClick={addFollowUpRow}
                    >
                      <AddIcon className="h-6 w-6" />
                    </Button>
                  )}

                {index > 0 && (
                  <Button
                    kind="secondary"
                    className="!min-w-fit"
                    type="button"
                    onClick={() => removeFollowUpRow(index)}
                  >
                    <CloseIcon className="h-6 w-6" />
                  </Button>
                )}
              </div>
            ))}

            {followUpRows.length === 3 && (
              <p className="text-xs text-gray-500">
                Maximum of 3 follow-ups allowed
              </p>
            )}
          </HookedForm>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 p-4 w-full sticky bottom-0">
        <div className="w-full px-4 flex justify-between items-center">
          <Button
            kind="secondary"
            type="button"
            onClick={handleBack}
            className="bg-secondary border min-w-fit ml-8"
          >
            <ArrowLeft />
          </Button>
          <div className="flex gap-2">
            <Button
              kind="secondary"
              type="button"
              onClick={() => handleSubmit(() => onSubmit('DRAFT'))()}
              disabled={isSubmitting}
              leftNode={<DraftIcon />}
            >
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={handleSubmit(() => onSubmit())}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Automation'}
            </Button>
          </div>
        </div>
      </div>

      {/*<pre style={{ border: 'solid 1px green', textAlign: 'left', maxWidth: '1200px', overflowX: 'scroll' }}>{ JSON.stringify({*/}
      {/*  EVENT_SOURCE_OPTIONS: EVENT_SOURCE_OPTIONS,*/}
      {/*  EVENT_OPTIONS: EVENT_NAME_OPTIONS,*/}
      {/*  selectedSource: selectedEventSource,*/}
      {/*  filteredEventOptions: filteredEventOptions,*/}
      {/*}, null, 4) }</pre>*/}
      {/*<pre style={{ border: 'solid 1px red', textAlign: 'left', maxWidth: '1200px', overflowX: 'scroll' }}>{ JSON.stringify({formValues: form.getValues()}, null, 4) }</pre>*/}

      {/* Follow-ups are sent with the same designs as the first message */}
      <DesignPreviewDialog
        open={previewChannel !== null}
        onClose={() => setPreviewChannel(null)}
        title={previewChannel === MessageTemplateChannel.SMS ? smsDesign.title : emailDesign.title}
        channel={previewChannel ?? MessageTemplateChannel.EMAIL}
        content={previewChannel === MessageTemplateChannel.SMS ? smsDesign.content : emailDesign.content}
      />
    </>
  );
};